import { BalanceSheetConfigColumn } from './BalanceSheetConfigPanel.types';
export interface OverflowResult {
  overflowColumnIds: Set<string>;
  hasOverflow: boolean;
  cutoffColumnId: string | null;
}

/**
 * Determines which non-total columns overflow the max visible column limit.
 *
 * Hidden (unchecked) columns above the capacity line don't consume a slot,
 * freeing space for columns further down. Once all slots are consumed, every
 * subsequent column is marked as overflow regardless of its visibility toggle.
 */
export function computeOverflowColumns({
  maxVisibleColumns,
  showInEstate,
  showOutOfEstate,
  inEstateTotalColumn,
  outOfEstateTotalColumn,
  hasGrandTotalColumn,
  orderedInEstate,
  orderedOutOfEstate,
  hiddenColumns
}: {
  maxVisibleColumns: number | null;
  showInEstate: boolean;
  showOutOfEstate: boolean;
  inEstateTotalColumn: BalanceSheetConfigColumn | null;
  outOfEstateTotalColumn: BalanceSheetConfigColumn | null;
  hasGrandTotalColumn: boolean;
  orderedInEstate: BalanceSheetConfigColumn[];
  orderedOutOfEstate: BalanceSheetConfigColumn[];
  hiddenColumns: Set<string>;
}): OverflowResult {
  if (maxVisibleColumns === null) {
    return {
      overflowColumnIds: new Set<string>(),
      hasOverflow: false,
      cutoffColumnId: null
    };
  }

  // Only count total columns whose section is actually enabled
  const visibleTotalSlots = (inEstateTotalColumn && showInEstate ? 1 : 0) + (outOfEstateTotalColumn && showOutOfEstate ? 1 : 0) + (hasGrandTotalColumn && showInEstate && showOutOfEstate ? 1 : 0);
  const availableNonTotalSlots = Math.max(0, maxVisibleColumns - visibleTotalSlots);

  // Walk columns in order, consuming slots only for non-hidden columns.
  // Once slots run out, ALL subsequent columns are overflow — including
  // unchecked ones — so they display the asterisk below the divider line.
  const overflow = new Set<string>();
  let slotsRemaining = availableNonTotalSlots;
  let lastFittingId: string | null = null;
  const walkGroup = (columns: BalanceSheetConfigColumn[]) => {
    for (const col of columns) {
      if (slotsRemaining > 0) {
        const isHidden = hiddenColumns.has(col.id);
        if (!isHidden) {
          slotsRemaining--;
          lastFittingId = col.id;
        }
        // Hidden columns above the line don't consume a slot
      } else {
        overflow.add(col.id);
      }
    }
  };
  if (showInEstate) walkGroup(orderedInEstate);
  if (showOutOfEstate) walkGroup(orderedOutOfEstate);

  // The cutoff column is the last non-hidden column that fits — it gets
  // a thicker bottom border to visually separate "above" from "below".
  const cutoffId = overflow.size > 0 ? lastFittingId : null;
  return {
    overflowColumnIds: overflow,
    hasOverflow: overflow.size > 0,
    cutoffColumnId: cutoffId
  };
}